import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { HelpCircle, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const TicketSubmissionWidget = ({ companyId, requesterName, requesterEmail }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: requesterName || '',
    email: requesterEmail || '',
    title: '',
    description: '',
    category: 'General',
    priority: 'medium'
  });

  const categories = [
    'Access Management',
    'Email',
    'Network',
    'Software Request',
    'Hardware',
    'General',
    'Other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyId) {
      toast.error('No company context. Please sign in.');
      return;
    }
    setIsSubmitting(true);

    try {
      const ticketData = {
        company_id: companyId,
        title: formData.title,
        description: `From: ${formData.name} (${formData.email})\n\n${formData.description}`,
        priority: formData.priority,
        category: formData.category,
        requester: formData.name
      };

      await api.createTicket(ticketData);
      
      toast.success('Ticket submitted! We\'ll get back to you soon.', {
        description: 'Most issues are resolved within 5 minutes.',
        duration: 5000
      });

      // Reset form (keep prefilled user identity if provided)
      setFormData({
        name: requesterName || '',
        email: requesterEmail || '',
        title: '',
        description: '',
        category: 'General',
        priority: 'medium'
      });
      
      setIsOpen(false);
    } catch (error) {
      toast.error('Failed to submit ticket', {
        description: 'Please try again or contact support.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      {/* Floating Help Button */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button 
            data-testid="ticket-help-launcher"
            className="fixed bottom-6 left-6 h-14 w-14 rounded-full shadow-xl bg-gray-900 hover:bg-gray-800 z-50"
            size="icon"
          >
            <HelpCircle className="w-6 h-6 text-white" />
          </Button>
        </DialogTrigger>

        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">Need Help? Submit a Ticket</DialogTitle>
            <DialogDescription>
              Tell us what's wrong and we'll fix it. Most issues resolved in under 5 minutes.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Name and Email */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-900 block mb-2">
                  Your Name *
                </label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="John Smith"
                  className="border-gray-300"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-900 block mb-2">
                  Your Email *
                </label>
                <Input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="john@company.com"
                  className="border-gray-300"
                />
              </div>
            </div>

            {/* Issue Title */}
            <div>
              <label className="text-sm font-medium text-gray-900 block mb-2">
                What's the problem? *
              </label>
              <Input
                required
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="e.g., Can't access my email"
                className="border-gray-300"
              />
            </div>

            {/* Category and Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-900 block mb-2">
                  Category
                </label>
                <Select value={formData.category} onValueChange={(value) => handleChange('category', value)}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-900 block mb-2">
                  How urgent?
                </label>
                <Select value={formData.priority} onValueChange={(value) => handleChange('priority', value)}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low - Can wait</SelectItem>
                    <SelectItem value="medium">Medium - Normal</SelectItem>
                    <SelectItem value="high">High - Need help now</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium text-gray-900 block mb-2">
                Tell us more (optional)
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Any additional details that might help us fix this faster..."
                rows={4}
                className="border-gray-300 resize-none"
              />
            </div>

            {/* AI Help Message */}
            <Card className="bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
              <CardContent className="p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-900 font-medium">
                    Our AI will try to solve this automatically
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    82% of tickets are resolved within 5 minutes without human intervention
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                size="lg"
              >
                {isSubmitting ? (
                  <>Submitting...</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Ticket
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={isSubmitting}
                className="border-gray-300"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TicketSubmissionWidget;
